import React, { useState } from "react";
import { MergeDataNode } from "../utils/mergeDataIndex";
import Highlight from "./Highlight";
import { useStyles } from "../styles";

const CLIP_AT = 300;

type MergeDataStyles = ReturnType<typeof useStyles>;

interface Props {
  node: MergeDataNode;
  expanded: boolean;
  query: string;
  styles: MergeDataStyles;
  onToggle: (id: string) => void;
  onCopy: (node: MergeDataNode) => void;
}

const MergeDataRow = ({
  node,
  expanded,
  query,
  styles,
  onToggle,
  onCopy,
}: Props) => {
  const [showAll, setShowAll] = useState(false);

  if (node.kind !== "value") {
    return (
      <button
        type="button"
        className={styles.row}
        aria-expanded={expanded}
        onClick={() => onToggle(node.id)}
      >
        <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
        <span className={styles.key}>
          <Highlight text={node.label} query={query} />
        </span>
        <span className={styles.badge}>({node.childIds.length})</span>
      </button>
    );
  }

  const value = node.displayValue ?? "";
  const clipped = !showAll && value.length > CLIP_AT;
  const displayText = clipped ? `${value.slice(0, CLIP_AT)}…` : value;

  return (
    <div>
      <button
        type="button"
        className={styles.row}
        title={`Click to copy ${node.templatePath} (${node.path})`}
        aria-label={`${node.label}: ${displayText}`}
        onClick={() => onCopy(node)}
      >
        <span className={styles.key}>
          <Highlight text={node.label} query={query} />:
        </span>{" "}
        <span className={styles.value}>
          <Highlight text={displayText} query={query} />
        </span>
      </button>
      {value.length > CLIP_AT && (
        <button
          type="button"
          className={styles.more}
          onClick={(event) => {
            event.stopPropagation();
            setShowAll((current) => !current);
          }}
        >
          {showAll ? "show less" : "show more"}
        </button>
      )}
    </div>
  );
};

export default React.memo(MergeDataRow);
